/**
 * Полный список permission-ключей приложения.
 *
 * Каждый ключ — строка вида `domain.action[.detail]`. Использовать
 * через `usePermissions().can('events.edit')`.
 *
 * Источник:
 *   - legacy `src/utils/consts/roles.ts` (ALLOWED_ROLES_FOR_MENU_ITEMS,
 *     ROLES_FOR_*, и т.п.)
 *   - матрица ролей в xlsx (см. README permissions)
 *
 * Карта `role → дефолтные ключи` живёт в `./roleDefaults.ts`.
 * Per-user override'ы (когда бэк подключим) — поверх дефолта роли.
 */

export const PERMISSION_KEYS = [
  /* ── Sidebar / menu items ────────────────────────────── */
  'menu.home',
  'menu.orders',
  'menu.booked',
  'menu.customers',
  'menu.reports',
  'menu.annualReports',
  'menu.events',
  'menu.theaters',
  'menu.regions',
  'menu.banners',
  'menu.paymentMethods',
  'menu.smsPlatforms',
  'menu.apiServices',
  'menu.users',
  'menu.cashiers',
  'menu.addUser',
  'menu.organizations',
  'menu.discounts',
  'menu.smsCodes',
  'menu.appStats',
  'menu.myPartners',
  'menu.mailingSend',
  'menu.mailingList',
  'menu.refund',
  'menu.refundClient',
  'menu.tax',
  'menu.tickets',
  'menu.contentPages',
  'menu.cashierJournal',

  /* ── Sales / tickets ─────────────────────────────────── */
  'sales.create',
  'sales.booking',
  'sales.checkout',
  'sales.invitation',
  'tickets.view',
  'tickets.delete',
  'tickets.distribute',
  'tickets.changeStatus',
  'tickets.changePriceVisible',

  /* ── Orders ──────────────────────────────────────────── */
  'orders.cancel',
  'orders.statusFilter',
  'orders.recheckPayment',

  /* ── Refunds ─────────────────────────────────────────── */
  'refunds.view',
  'refunds.viewClient',
  'refunds.confirm',
  'refunds.reject',

  /* ── Discounts / Promocodes ──────────────────────────── */
  'discounts.view',
  'discounts.create',
  'discounts.toggle',

  /* ── Events ──────────────────────────────────────────── */
  'events.view',
  'events.create',
  'events.edit',
  'events.cancel',
  'events.draft',
  'events.archive',
  'events.closed',
  'events.filters',
  'events.session.create',
  'events.session.toggleVisibility',
  'events.changeToInvitation',
  'events.changeInvitationStatus',
  'events.serviceFee',
  'events.qrToggle',
  'events.dailyScan',
  'events.esbo.import',
  'events.esbo.changeScheme',

  /* ── Reports / export ────────────────────────────────── */
  'reports.statistic',
  'reports.duplicate',
  'reports.commissions',
  'reports.annual.view',
  'reports.annual.export',
  'export.general',
  'export.payments',
  'export.events',
  'export.cancelledTickets',

  /* ── Users management ────────────────────────────────── */
  'users.view',
  'users.add',
  'users.resetPassword',
  'users.changeInvitationStatus',
  'users.create.admin',
  'users.create.manager',
  'users.create.organizer',
  'users.create.cashier',
  'users.create.controller',
  'users.create.accountant',
  'users.create.super_cashier',
  'users.create.artist',
  'users.create.partner',

  /* ── Cashiers / cashier report ───────────────────────── */
  'cashiers.report.view',
  'cashiers.report.export',

  /* ── Organizations management ────────────────────────── */
  'organizations.write',

  /* ── Sales channels (бывш. «Мои партнёры») ───────────── */
  'partners_my.write',

  /* ── Payment methods (Финансы → Способы оплаты) ──────── */
  'payment_methods.view',
  'payment_methods.write',

  /* ── Tax (Финансы → Налоговая) ───────────────────────── */
  /* `view` открывает раздел; `write` — правка полей и «Отправить». */
  'finance.tax.view',
  'finance.tax.write',

  /* ── Banners (Маркетинг → Баннеры) ───────────────────── */
  'banners.view',
  'banners.write',

  /* ── Regions (Каталог → Регионы) ─────────────────────── */
  /* `view` открывает раздел; `write` — create/edit/delete. */
  'catalog.regions.view',
  'catalog.regions.write',

  /* ── Theaters (Каталог → Площадки) ───────────────────── */
  /* `view` открывает раздел и детальную; `write` — create/edit. */
  'catalog.theaters.view',
  'catalog.theaters.write',

  /* ── Mailing (Маркетинг → Рассылки) ──────────────────── */
  /* `view` открывает раздел (обе вкладки). `send` — отправка. */
  'mailing.view',
  'mailing.send',

  /* ── Customers (Продажи → Клиенты) ───────────────────── */
  /* `view` открывает раздел и детальную; `write` — пометки/заметки
     (если БЕ дополнит); `block` — деактивация клиента. */
  'customers.view',
  'customers.write',
  'customers.block',

  /* ── Misc ────────────────────────────────────────────── */
  'org.switch',
  'partners.work',
  'theater.read',
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

// Узкие подмножества — для типобезопасных хелперов в UI.
export type EventPermissionKey = Extract<PermissionKey, `events.${string}`>;
export type MenuPermissionKey = Extract<PermissionKey, `menu.${string}`>;
